import React from 'react';
import { useSelector } from 'react-redux';
import debounce from 'lodash/debounce';
import Luminary, { VideoData } from '@lumen5/luminary';

import { CreationSourceEnum } from 'js/src/client';
import { loadProjects } from 'js/src/data/account';
import {
    setAutoCreateSceneGroupFromUploadedAssetUuid,
    setWindowWidth,
    updateShouldShowLeaveWarning,
} from 'js/src/data/app';
import { video_id } from 'js/src/data/app/app-reducers';
import {
    getAutoCreateSceneGroupFromUploadedAssetUuid,
    getSelectedTemplateId,
    getVideoCreationSource,
    getVideoId,
    getVideoSlug,
} from 'js/src/data/app/app-selectors';
import { loadVideoComments } from 'js/src/data/comments';
import { isTemplateBundleLoading, loadTemplateAndSceneDesigns } from 'js/src/data/templates';
import { loadVideoTranslations } from 'js/src/data/translations';
import { UploadingContext } from 'js/src/data/uploads';
import { loadUsageLimits } from 'js/src/data/usage-limit/usage-limit-operations';
import { createImpersonateUrl, ErrorReporter } from 'js/src/libs/error-reporter';
import Flags from 'js/src/libs/flags';
import { getQueryParam } from 'js/src/libs/querystring';
import type { RootState } from 'js/src/libs/RootState';
import { useAppDispatch } from 'js/src/libs/RootState';
import uuid from 'js/src/libs/uuid';
import { useLuminaryContext } from 'js/src/luminary/luminary-context-provider';
import { useLuminaryOperations, useLuminarySelector } from 'js/src/luminary/luminary-hooks';
import {
    applyUploadingMediaToSceneGroupOperation,
    createNewVoicedrivenSceneGroupOperation,
    initializeSceneAttributesCacheOperation,
} from 'js/src/luminary/luminary-operations';
import { getVideoSizeAnalyticsProperties } from 'js/src/luminary/luminary-utils';
import { getAnalyticsSessionId, getStageDurationProperties } from 'js/src/libs/analytics-session';

export const isTemplateValid = (templateId: string) => {
    // when luminary initializes without a template, luminary.template.id is a uuid
    // this is not a valid/real template.
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return !uuidRegex.test(templateId);
};

export const useApiDataForCreator = () => {
    const dispatch = useAppDispatch();
    const videoId = useSelector(getVideoId);
    const videoSlug = useSelector(getVideoSlug);
    const videoCreationSource = useSelector(getVideoCreationSource);
    const templateId = useLuminarySelector((luminary) => luminary.template.id, []);

    React.useEffect(() => {
        // load stuff that doesn't depend on anything
        dispatch(loadProjects());
    }, [dispatch]);

    React.useEffect(() => {
        if (videoId) {
            try {
                ErrorReporter.setTag('video_id', videoId);
            } catch {
                // setTag may not be available in test environments
            }
        }
    }, [videoId]);

    React.useEffect(() => {
        // load stuff that depends on videoId only
        if (!videoId) {
            return;
        }
        dispatch(loadUsageLimits(videoId));
        dispatch(loadVideoComments(videoId, videoSlug));
    }, [dispatch, videoId, videoSlug]);

    React.useEffect(() => {
        // load stuff that depends on videoCreationSource and videoId
        if (!videoId) {
            return;
        }
        if (videoCreationSource === CreationSourceEnum.TRANSLATION) {
            dispatch(loadVideoTranslations(videoId));
        }
    }, [dispatch, videoCreationSource, videoId]);

    React.useEffect(() => {
        // load stuff that depends on templateId
        if (templateId && isTemplateValid(templateId)) {
            dispatch(loadTemplateAndSceneDesigns(templateId));
        }
    }, [dispatch, templateId]);

    return null;
};

export const useVideoCreatorInitialization = ({ isEditingDraft }: { isEditingDraft: boolean }) => {
    const { initializeSceneAttributesCache } = useLuminaryOperations({
        initializeSceneAttributesCacheOperation,
    });
    const loadEventSent = React.useRef(false);
    const aspectRatio = useLuminarySelector((luminary) => luminary.aspectRatio, []);
    const videoId = useSelector(getVideoId);
    const videoCreationSource = useSelector(getVideoCreationSource);
    const luminary = useLuminaryContext();
    const interactableEventSent = React.useRef(false);
    const creatorMountedAt = React.useRef(Date.now());

    React.useEffect(() => {
        if (loadEventSent.current) {
            return;
        }
        loadEventSent.current = true;
        analytics.track('Loaded Creator', {
            aspect_ratio: aspectRatio,
            is_edit: isEditingDraft,
            video_id: video_id,
            isWebGLSupported: Luminary.isWebGLSupported,
            isSlowWebGLSupported: Luminary.isSlowWebGLSupported,
        });
    }, [aspectRatio, isEditingDraft]);

    React.useEffect(() => {
        // Fire once a freshly-generated Toolbox video has actually rendered to the
        // canvas and is interactable (i.e. the user could preview/play/edit it).
        // Distinct from 'Loaded Creator', which fires when the Creator shell mounts,
        // before any pixels render.
        //
        // Scoped to the Toolbox flow (Flags.canUseToolbox) and to first generation
        // only (!isEditingDraft) — we don't want to count sessions that open an
        // existing draft to edit. The Toolbox path populates video_id late (after
        // the agent's turn-end persistence), so we also wait for a real video_id;
        // this keeps the event keyed to a persisted video and lets us distinguish
        // "the video became usable" from "the agent is still building" when
        // analyzing where users drop off.
        if (interactableEventSent.current || !Flags.canUseToolbox() || isEditingDraft || !videoId) {
            return;
        }
        let cancelled = false;
        luminary
            .onAllSlideImagesRendered()
            .then(() => {
                if (cancelled || interactableEventSent.current) {
                    return;
                }
                interactableEventSent.current = true;
                analytics.track('Toolbox Video Interactable', {
                    video_id: videoId,
                    aspect_ratio: aspectRatio,
                    video_creation_source: videoCreationSource,
                    session_id: getAnalyticsSessionId(),
                    ...getVideoSizeAnalyticsProperties(luminary),
                    // includeElapsedStage measures the review too: it has no successor
                    // stage to close its bracket, but the video being on screen ends it.
                    ...getStageDurationProperties({ includeElapsedStage: true }),
                    time_to_interactable_ms: Date.now() - creatorMountedAt.current,
                });
            })
            .catch(() => {
                // do nothing — the video never became interactable
            });
        return () => {
            cancelled = true;
        };
    }, [luminary, videoId, aspectRatio, isEditingDraft, videoCreationSource]);

    React.useEffect(() => {
        const timeout = window.setTimeout(() => {
            if (getQueryParam('disable_cache') !== 'true') {
                initializeSceneAttributesCache();
            }
        }, 3000);
        return () => {
            window.clearTimeout(timeout);
        };
    }, [initializeSceneAttributesCache]);

    React.useEffect(() => {
        // update the impersonate url, since creating a new video updates the url without a page reload
        const user = ErrorReporter.getUser();
        ErrorReporter.setUser({
            ...user,
            impersonate: createImpersonateUrl(SERVER.USER.email),
        });
    }, []);

    React.useEffect(() => {
        const debouncedWithArgs = (func: (...args: unknown[]) => void, wait: number) => {
            const argsList: unknown[] = [];
            const debouncedWrapper = debounce((argsList) => {
                func(argsList);
                argsList = [];
            }, wait);
            return function (...args: unknown[]) {
                argsList.push(args);
                debouncedWrapper(argsList);
            };
        };
        const translateCallback = (...argsList: unknown[]) => {
            ErrorReporter.addBreadcrumb({
                category: 'ui',
                message: 'Enable Page Translation Detected',
                data: {
                    numNodes: argsList.length,
                    confidence:
                        argsList.length < 5 ? 'LOW' : argsList.length < 50 ? 'MODERATE' : 'HIGH',
                },
            });
        };
        const untranslateCallback = (...argsList: unknown[]) => {
            ErrorReporter.addBreadcrumb({
                category: 'ui',
                message: 'Disable Page Translation Detected',
                data: {
                    numNodes: argsList.length,
                    confidence:
                        argsList.length < 5 ? 'LOW' : argsList.length < 50 ? 'MODERATE' : 'HIGH',
                },
            });
        };
        const debouncedWithArgsTranslateCallback = debouncedWithArgs(translateCallback, 500);
        const debouncedWithArgsUntranslateCallback = debouncedWithArgs(untranslateCallback, 500);
        const observer = new MutationObserver((mutationsList) => {
            for (const mutation of mutationsList) {
                if (mutation.type === 'childList') {
                    for (const addedNode of Array.from(mutation.addedNodes)) {
                        if (addedNode.nodeName === 'FONT') {
                            debouncedWithArgsTranslateCallback(addedNode);
                        }
                    }
                    for (const removedNode of Array.from(mutation.removedNodes)) {
                        if (removedNode.nodeName === 'FONT') {
                            debouncedWithArgsUntranslateCallback(removedNode);
                        }
                    }
                }
            }
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true,
        });
        return () => {
            observer.disconnect();
        };
    }, []);

    return null;
};

export const useLeaveWarningMessage = ({ needsSaving }: { needsSaving: () => boolean }) => {
    React.useEffect(() => {
        // avoid accidental departures - initialize to true
        updateShouldShowLeaveWarning(true);
    }, []);

    React.useEffect(() => {
        // update as required
        if (needsSaving()) {
            updateShouldShowLeaveWarning(true);
        } else {
            updateShouldShowLeaveWarning(false);
        }
    }, [needsSaving]);

    return null;
};

export const useWindowWidthObserver = () => {
    const dispatch = useAppDispatch();

    React.useEffect(() => {
        const measureWindowDebounced = debounce(() => {
            dispatch(setWindowWidth({ width: window.innerWidth }));
        }, 500);
        dispatch(setWindowWidth({ width: window.innerWidth }));
        window.addEventListener('resize', measureWindowDebounced);
        return () => {
            window.removeEventListener('resize', measureWindowDebounced);
        };
    }, [dispatch]);

    return null;
};

export const useAutoCreateSceneGroupFromUploadedAssetUuid = () => {
    const autoCreateSceneGroupFromUploadedAssetUuid = useSelector(
        getAutoCreateSceneGroupFromUploadedAssetUuid,
    );
    const dispatch = useAppDispatch();
    const didRun = React.useRef(false);
    const { createNewVoicedrivenSceneGroup, applyUploadingMediaToSceneGroup } =
        useLuminaryOperations({
            createNewVoicedrivenSceneGroupOperation,
            applyUploadingMediaToSceneGroupOperation,
        });
    const selectedTemplateId = useSelector(getSelectedTemplateId);
    const templateId = useLuminarySelector((luminary) => luminary.template.id, []);
    const templateBundleLoading = useSelector((state: RootState) =>
        isTemplateBundleLoading(state, templateId),
    );
    const templateBundleSet = useLuminarySelector((luminary) => luminary.isTemplateBundleSet, []);
    const ready = templateId === selectedTemplateId && !templateBundleLoading && templateBundleSet;
    React.useEffect(() => {
        (async () => {
            if (didRun.current) {
                return;
            }
            if (!ready) {
                return;
            }
            if (!autoCreateSceneGroupFromUploadedAssetUuid) {
                return;
            }
            didRun.current = true;
            dispatch(setAutoCreateSceneGroupFromUploadedAssetUuid({ uuid: null }));
            const parentActionGroup = uuid();
            const systemTriggered = true;
            const { sceneGroupId } = createNewVoicedrivenSceneGroup(
                VideoData.CATEGORY_TALKING_HEAD,
                0,
                {
                    isScreenRecording: true,
                    deleteOtherScenes: true,
                },
                parentActionGroup,
                systemTriggered,
            );

            const uploadingPromise = Promise.resolve([autoCreateSceneGroupFromUploadedAssetUuid]);
            await applyUploadingMediaToSceneGroup({
                uploadingPromise,
                uploadingContext: UploadingContext.ScreenRecording,
                sceneGroupId,
                recommendOverlays: false,
                systemTriggered,
                parentActionGroup,
            });
        })();
    }, [
        applyUploadingMediaToSceneGroup,
        autoCreateSceneGroupFromUploadedAssetUuid,
        createNewVoicedrivenSceneGroup,
        dispatch,
        ready,
    ]);
    return null;
};
